import { Component, OnDestroy, OnInit } from "@angular/core";
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";

import Swal from "sweetalert2";
import { AuthService } from "../../services/auth.service";
import { CommonModule } from "@angular/common";
import { NgxMaskDirective, provideNgxMask } from "ngx-mask";

type ViewMode = "login" | "firstAccess";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
  imports: [CommonModule, ReactiveFormsModule, NgxMaskDirective],
  providers: [provideNgxMask()],
})
export class LoginComponent implements OnInit, OnDestroy {
  formLogin!: UntypedFormGroup;
  formFirstAccess!: UntypedFormGroup;

  loading = false;
  firstAccessLoading = false;
  firstAccessSuccess = false;
  message = "";

  view: ViewMode = "login";

  constructor(
    private formBuilder: UntypedFormBuilder,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.createForms();
  }

  ngOnDestroy(): void {}

  private createForms(): void {
    this.formLogin = this.formBuilder.group({
      email: ["", Validators.required],
      password: ["", Validators.required],
    });

    this.formFirstAccess = this.formBuilder.group({
      email: ["", [Validators.required, Validators.email]],
      document: ["", Validators.required],
    });
  }

  setView(view: ViewMode): void {
    this.view = view;
    this.firstAccessSuccess = false;
    this.message = "";
    this.formFirstAccess.reset();
  }

  async login(): Promise<void> {
    if (this.formLogin.invalid) {
      this.formLogin.markAllAsTouched();
      return;
    }
    this.loading = true;
    await this.authService
      .login(this.formLogin.value)
      .then(() => {
        this.loading = false;
      })
      .catch((error) => {
        this.loading = false;
        switch (error?.code) {
          case "auth/wrong-password":
          case "auth/invalid-credential":
          case "auth/user-not-found":
            this.message = "E-mail ou senha incorretos. Verifique seus dados e tente novamente.";
            break;
          case "auth/invalid-email":
            this.message = "O e-mail informado não é válido. Verifique e tente novamente.";
            break;
          case "auth/user-disabled":
            this.message = "Esta conta foi desativada. Entre em contato com o suporte.";
            break;
          case "auth/too-many-requests":
            this.message = "Muitas tentativas de login. Aguarde alguns minutos e tente novamente.";
            break;
          case "auth/network-request-failed":
            this.message = "Sem conexão com a internet. Verifique sua rede e tente novamente.";
            break;
          default:
            this.message = "Não foi possível realizar o login. Tente novamente mais tarde.";
            break;
        }

        Swal.mixin({
          customClass: {
            confirmButton: "btn btn-success",
            cancelButton: "btn btn-danger",
          },
          buttonsStyling: false,
        }).fire({
          text: this.message,
          icon: "error",
          confirmButtonText: "OK",
        });
      });
  }

  async sendFirstAccess(): Promise<void> {
    if (this.formFirstAccess.invalid) {
      this.formFirstAccess.markAllAsTouched();
      return;
    }

    const { email, document } = this.formFirstAccess.value;
    this.firstAccessLoading = true;

    try {
      await this.authService.sendFirstAccessEmail(email, document);
      this.firstAccessSuccess = true;
    } catch (error: any) {
      const swal = Swal.mixin({
        customClass: { confirmButton: "btn btn-default" },
        buttonsStyling: false,
      });

      if (error?.code === "COMPANY_NOT_FOUND") {
        swal.fire({
          title: "Cadastro não encontrado",
          text: "Não localizamos nenhum cadastro com esse e-mail e CPF/CNPJ. Verifique os dados informados.",
          icon: "warning",
          confirmButtonText: "OK",
        });
      } else {
        swal.fire({
          title: "Erro ao enviar o e-mail",
          text: "Ocorreu um problema ao processar sua solicitação. Tente novamente ou entre em contato com o suporte.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } finally {
      this.firstAccessLoading = false;
    }
  }
}
